// Door.ts — PURE procedural door model for the Buildings Set1 opening layer (§4.4). The model is
// authored in the OPENING-SEGMENT LOCAL frame:
//
//     x = along the wall (0 = the opening's center),
//     y = UP (0 = the floor slab top),
//     z = OUTWARD (0 = the interior face of the wall),
//
// and then transformed into building-local coordinates by the caller (via the segment frame
// basis). This keeps the model free of any world/building rotation knowledge.
//
// Swing semantics (CORRECTED 2026-08-24 — Set1_DoorModelCorrectness §1.2):
//   - HINGE STILE: "Left"/"Right" names the hinge stile (the −x / +x end of the opening in the
//     model frame, where x is along the wall).
//   - FLUSH FACE: the slab is flush with the face it swings TOWARD. `in*` swings into the room
//     (−z, the interior side) ⇒ the slab is flush with the INNER face (z ≈ 0); `out*` swings into
//     the exterior (+z) ⇒ the slab is flush with the OUTER face (z ≈ wallThickness). See §1.2 for
//     the two independent derivations (the >90° clearance argument and the doorstop-side argument).
//   - DOORSTOP: a rib (STOP_DEPTH along z × STOP_PROJ into the opening) runs along both jambs and
//     the head, on the side of the slab OPPOSITE the swing (it resists motion past closed). It is
//     load-bearing for the corrected rule: an `in*` slab flush inner leaves the outer part of the
//     reveal open, and the rib is what the slab closes against.
//   - openAngle (degrees, 0 = closed) rotates the slab around the vertical hinge axis at the
//     hinge-barrel z (the swing-side face of the slab: ≈ 0 for `in*`, ≈ wallThickness for `out*`),
//     so the hinge edge stays put while the door opens; the sign follows the swing direction
//     (into the room for `in*`, into the exterior for `out*`). The rotation only visibly applies
//     when openAngle != 0 (the common default-closed case stays axis-aligned in the opening).
//
// Parts: a non-overlapping frame (2 jambs + head + threshold spanning only between the jambs,
// filling the full wallThickness reveal), the doorstop rib, a 2-panel slab border of 5
// non-overlapping boxes + 2 recessed panels, and hardware on BOTH faces (escutcheon + stem +
// lever mirrored on the interior and exterior faces, D2) plus 3 hinge leaves spanning the
// slab↔jamb gap on the hinge stile. UVs are metre-scaled so procedural materials tile
// continuously. Groups are tagged with SEMANTIC slots doorFrame/doorSlab/doorHardware (the
// renderer maps each → a recipe id).
//
// Pure: no three / mobx-graphlink / DOM imports — the Node vitest lane imports this directly.
// (`BuildingShared.ts` is itself pure data — no observable/`@Field`/mobx-graphlink — so importing
// it keeps this module Node-testable.)
import {MeshBuilder, BoxMesh, TransformPlanToBasis, type MeshPlan, type V3} from "./MeshPlan.js";
import {DoorSwing} from "../../Server/DB/mapObjects/@MapObject/ObjModifiers/Building/BuildingShared.js";

export type DoorModelParams = {
	width: number;
	height: number;
	swing: string;      // a `DoorSwing` value ("outLeft"/"outRight"/"inLeft"/"inRight")
	openAngle: number;  // degrees
	/** Wall thickness in metres (the reveal depth the frame sits in). */
	wallThickness: number;
};

// ---- Input-validation defaults (bug I): a malformed/absent persisted field can no longer
// silently collapse the geometry (repo "silently-disabled guards" note).
const DEFAULT_WIDTH = 0.9;        // metres
const DEFAULT_HEIGHT = 2.05;      // metres
const DEFAULT_WALL_THICKNESS = 0.2; // metres
const DEFAULT_SWING: DoorSwing = DoorSwing.inRight;

/** Resolve a positive, finite dimension; on any invalid input fall back to `fallback`. */
function ResolveDimension(value: number|n, name: string, fallback: number): number {
	return (typeof value == "number" && Number.isFinite(value) && value > 0) ? value : fallback;
}

/** Resolve swing against the four `DoorSwing` values; anything else (legacy `""`) → `DEFAULT_SWING`,
 *  so the `swing: ""` placeholder can no longer silently mean `outRight` (bug I). */
function ResolveSwing(value: string|n): DoorSwing {
	return (typeof value == "string" && (value as DoorSwing) in DoorSwing) ? value as DoorSwing : DEFAULT_SWING;
}

/** Planar basis positioning the model in building-local space. `t`=along-wall, `up`=up, `n`=wall OUTWARD normal, `pos`=model-frame origin (inner face at opening center). maps x→t, y→up, z→n. */
export type OpeningBasis = {
	t: V3;
	n: V3;
	up: V3;
	pos: V3;
};

const SLAB_THICKNESS = 0.045; // metres — the door slab depth (along z)
const SLAB_CLEARANCE = 0.004; // per-side slab-to-frame gap
// Frame / clear-opening dimensions (bug B — the slab must be sized to the CLEAR opening, not the
// rough opening; the frame consumes these widths/heights on every side).
const JAMB_W = 0.08;          // jamb (door stop) width along x, per side
const HEAD_H = 0.08;          // head casing height along y
const THRESHOLD_H = 0.02;     // threshold height along y (above the floor slab top)
const PANEL_DEPTH = 0.02;     // depth the recessed panel boxes sit below the slab's visible face
// Doorstop rib (§1.3) — sits on the NON-swing side of the slab, along both jambs + the head. It
// must not overlap the jamb/head lining boxes (bug F).
const STOP_DEPTH = 0.018;     // rib depth along z
const STOP_PROJ = 0.012;      // rib projection into the opening from the jamb/head lining face
const ESCUTCHEON_DEPTH = 0.006; // escutcheon plate depth along z
const LEVER_STANDOFF = 0.012;   // plate-front → lever-bar-center standoff (the stem's length)
const STILE_WIDTH = 0.1;      // slab left/right stile width + top/bottom rail height
const MID_RAIL_WIDTH = 0.08;  // mid-rail height (between the two panels)
const HANDLE_RADIUS = 0.014;  // lever handle thickness
const HANDLE_REACH = 0.09;    // lever length
const HANDLE_HEIGHT = 1.0;    // handle center height above the floor
const ESCUTCHEON = 0.05;      // escutcheon plate size
const ESCUTCHEON_INSET = 0.06;// plate outer edge inset from the latch edge (bug G)
const STEM_X = 0.012;         // handle-stem cross-section (square neck linking plate → lever)
const HINGE_SIZE = 0.02;      // hinge box thickness
const HINGE_HEIGHT = 0.12;    // hinge box height
const HINGE_FRACS = [0.1, 0.5, 0.9]; // hinge leaf vertical fractions (near top / middle / bottom)

/** Build the door model in opening-segment-local frame, then place it in building-local space via
 *  `basis`. Groups are tagged doorFrame/doorSlab/doorHardware. */
export function BuildDoorModel(params: DoorModelParams, basis: OpeningBasis): MeshPlan {
	const local = BuildDoorModelLocal(params);
	return TransformPlanToBasis(local, {t: basis.t, up: basis.up, n: basis.n, pos: basis.pos});
}

/** Build the door model in the opening-segment-local frame only (no placement). Pure +
 *  node-testable without a wall. Exposed for the unit tests. */
export function BuildDoorModelLocal(params: DoorModelParams): MeshPlan {
	// Resolve-then-compare input validation (bug I). FIRST, so every consumer below compares against
	// a validated value, never a raw possibly-undefined/NaN field.
	const w = ResolveDimension(params.width, "width", DEFAULT_WIDTH);
	const h = ResolveDimension(params.height, "height", DEFAULT_HEIGHT);
	const wt = ResolveDimension(params.wallThickness, "wallThickness", DEFAULT_WALL_THICKNESS);
	const swing = ResolveSwing(params.swing);
	// Clamp openAngle to a sane [0, 180°) range so a bogus persisted value (e.g. 300°) can't sweep
	// the slab all the way through the wall and out the far side.
	const openAngle = (typeof params.openAngle == "number" && Number.isFinite(params.openAngle))
		? Math.min(Math.max(params.openAngle, 0), 180) : 0;

	const b = new MeshBuilder();

	// ---- Clear opening: the region inside the frame the slab must fit (bug B). The frame
	// consumes JAMB_W per side in x, HEAD_H at the top, THRESHOLD_H at the bottom.
	const clear = {
		x0: -w / 2 + JAMB_W,
		x1: w / 2 - JAMB_W,
		y0: THRESHOLD_H,
		y1: h - HEAD_H,
	};
	const clearW = clear.x1 - clear.x0; // w - 2*JAMB_W
	const clearH = clear.y1 - clear.y0; // h - HEAD_H - THRESHOLD_H

	const hingeSide = HingeSide(swing); // -1 = left (−x), +1 = right (+x)
	// Slab is flush with the face it swings TOWARD (§1.2): `in*` swings −z ⇒ flush INNER (z≈0); `out*` swings +z ⇒ flush OUTER (z≈wallThickness). `wt` is the full reveal depth (D1).
	const inSwing = swing.startsWith("in");
	const slabDepth = SLAB_THICKNESS;
	const slabZ = inSwing ? 0 : wt - slabDepth;

	// ---- Frame: 2 jamb casings + head + threshold, filling the whole wallThickness reveal (D1),
	// with head/threshold spanning ONLY between the jambs (bug F — no corner double-cover).
	b.append(BoxMesh([-w / 2, 0, 0], [JAMB_W, h, wt], "doorFrame"));                // left jamb
	b.append(BoxMesh([clear.x1, 0, 0], [JAMB_W, h, wt], "doorFrame"));              // right jamb
	b.append(BoxMesh([clear.x0, clear.y1, 0], [clearW, HEAD_H, wt], "doorFrame"));  // head (between jambs)
	b.append(BoxMesh([clear.x0, 0, 0], [clearW, THRESHOLD_H, wt], "doorFrame"));    // threshold (between jambs)

	// ---- Doorstop rib (§1.3): on the NON-swing side of the slab, along both jambs + the head.
	// `in*`→outward, `out*`→inward of the slab; ribs must not overlap the lining or the slab (bug F/B).
	const stopZ = inSwing ? slabZ + slabDepth : slabZ - STOP_DEPTH;
	const stopH = clearH - 2 * STOP_PROJ; // jamb stops leave room for the head stop corners
	b.append(BoxMesh([clear.x0, clear.y0 + STOP_PROJ, stopZ], [STOP_PROJ, stopH, STOP_DEPTH], "doorFrame"));       // left stop
	b.append(BoxMesh([clear.x1 - STOP_PROJ, clear.y0 + STOP_PROJ, stopZ], [STOP_PROJ, stopH, STOP_DEPTH], "doorFrame")); // right stop
	b.append(BoxMesh([clear.x0 + STOP_PROJ, clear.y1 - STOP_PROJ, stopZ], [clearW - 2 * STOP_PROJ, STOP_PROJ, STOP_DEPTH], "doorFrame")); // head stop

	// ---- Slab: sized to the CLEAR opening minus clearance (bug B), offset up by slabY0 (bug B),
	// pinned to the swing-side face (bug C2). Built as its own plan so it rotates independently.
	const slabX0 = clear.x0 + SLAB_CLEARANCE;
	const slabX1 = clear.x1 - SLAB_CLEARANCE;
	const slabW = slabX1 - slabX0;
	const slabY0 = THRESHOLD_H + SLAB_CLEARANCE;
	const slabH = clearH - 2 * SLAB_CLEARANCE;
	const left = slabX0; // == -slabW / 2 (the clear opening is centered on x = 0)
	const slabB = new MeshBuilder();
	const s = (x0: number, y0: number, sw: number, sh: number, z: number): MeshPlan =>
		BoxMesh([x0, y0, z], [sw, sh, slabDepth], "doorSlab");
	slabB.append(s(left, slabY0, STILE_WIDTH, slabH, slabZ));                                          // left stile
	slabB.append(s(left + slabW - STILE_WIDTH, slabY0, STILE_WIDTH, slabH, slabZ));                    // right stile
	slabB.append(s(left + STILE_WIDTH, slabY0 + slabH - STILE_WIDTH, slabW - 2 * STILE_WIDTH, STILE_WIDTH, slabZ)); // top rail (between stiles)
	slabB.append(s(left + STILE_WIDTH, slabY0, slabW - 2 * STILE_WIDTH, STILE_WIDTH, slabZ));          // bottom rail (between stiles)
	// Panels y-range, bounded by the bottom rail (below) and top rail (above).
	const panelsY0 = slabY0 + STILE_WIDTH;                    // bottom panel top edge
	const panelsY1 = slabY0 + slabH - STILE_WIDTH;            // top panel bottom edge
	const midRailY = (panelsY0 + panelsY1) / 2 - MID_RAIL_WIDTH / 2;
	slabB.append(s(left + STILE_WIDTH, midRailY, slabW - 2 * STILE_WIDTH, MID_RAIL_WIDTH, slabZ));     // mid-rail (between stiles)
	// Recessed panels: symmetric + unconditional inset from the slab's visible faces (bug F).
	const panelX0 = left + STILE_WIDTH;
	const panelX1 = left + slabW - STILE_WIDTH;
	const panelZ = slabZ + (slabDepth - PANEL_DEPTH) / 2;
	slabB.append(BoxMesh([panelX0, panelsY0, panelZ], [panelX1 - panelX0, midRailY - panelsY0, PANEL_DEPTH], "doorSlab"));
	slabB.append(BoxMesh([panelX0, midRailY + MID_RAIL_WIDTH, panelZ], [panelX1 - panelX0, panelsY1 - (midRailY + MID_RAIL_WIDTH), PANEL_DEPTH], "doorSlab"));
	// Hinge stile x + latch stile x (hoisted — used by the rotation and the hardware).
	const hingeX = hingeSide == -1 ? left : left + slabW;
	const latchX = hingeSide == -1 ? left + slabW : left;

	// ---- Slab + all slab-MOUNTED hardware built into ONE sub-plan so they rotate together (bug E).
	// Hardware: escutcheon + stem + lever on BOTH faces (D2) + hinge leaves on the hinge stile.
	const moving = new MeshBuilder();
	moving.append(slabB.build());
	// `latchDir = -hingeSide` names the latch end absolutely (bug G/H — branch on the end, never on
	// the sign of a coordinate). Hardware is placed relative to the latch edge; the lever extends
	// INWARD (toward the slab centre) in both hinge cases.
	const latchDir = -hingeSide;
	// The plate is inset from the latch edge by ESCUTCHEON_INSET and extends one plate-width inward
	// (toward the slab centre). plateCenter sits at latchX − latchDir·(INSET + ESC/2), so the box
	// spans [latchEdge ∓ INSET ∓ ESC .. latchEdge ∓ INSET] in both hinge cases.
	const plateCenter = latchX - latchDir * (ESCUTCHEON_INSET + ESCUTCHEON / 2);
	const plateX0 = plateCenter - ESCUTCHEON / 2;      // box min-x (extends inward one width)
	// Lever: a bar extending inward from the plate centre by HANDLE_REACH (contiguous slab → plate →
	// stem → lever). leverX0 shifts so the bar runs toward the slab centre, not past the latch edge.
	const leverX0 = plateCenter - (latchDir > 0 ? HANDLE_REACH : 0);

	// ---- Hardware on BOTH faces, mirrored across the slab's mid-plane (D2). Each face's assembly
	// stands OUTWARD from its slab face: plate (ESCUTCHEON_DEPTH) flush on the face, then the stem
	// (LEVER_STANDOFF), then the lever bar. `mirror=+1` → z=slabZ face (protrudes −z); `mirror=-1`.
	for (const mirror of [1, -1] as const) {
		const zBase = mirror == 1 ? slabZ : slabZ + slabDepth; // the slab face this assembly mounts on
		const plateZMin = mirror == 1 ? zBase - ESCUTCHEON_DEPTH : zBase;
		const stemZMin = mirror == 1 ? zBase - ESCUTCHEON_DEPTH - LEVER_STANDOFF : zBase + ESCUTCHEON_DEPTH;
		const leverZMin = mirror == 1 ? zBase - ESCUTCHEON_DEPTH - LEVER_STANDOFF - HANDLE_RADIUS : zBase + ESCUTCHEON_DEPTH + LEVER_STANDOFF - HANDLE_RADIUS;
		// Escutcheon plate.
		moving.append(BoxMesh([plateX0, HANDLE_HEIGHT - ESCUTCHEON / 2, plateZMin], [ESCUTCHEON, ESCUTCHEON, ESCUTCHEON_DEPTH], "doorHardware"));
		// Stem (the neck joining the plate to the lever, at the plate centre).
		moving.append(BoxMesh([plateCenter - STEM_X / 2, HANDLE_HEIGHT - STEM_X / 2, stemZMin], [STEM_X, STEM_X, LEVER_STANDOFF], "doorHardware"));
		// Lever bar (extends inward, centred on the stem in x).
		moving.append(BoxMesh([leverX0, HANDLE_HEIGHT - HANDLE_RADIUS, leverZMin], [HANDLE_REACH, 2 * HANDLE_RADIUS, 2 * HANDLE_RADIUS], "doorHardware"));
	}
	// Hinges: leaves centred on the pivot axis x (the slab hinge edge) so they sit in the slab↔jamb
	// gap at the swing-side face (bug H), at near-top / middle / near-bottom fractions (bug I).
	for (const frac of HINGE_FRACS) {
		const hy = slabY0 + slabH * frac;
		moving.append(BoxMesh([hingeX - HINGE_SIZE / 2, hy - HINGE_HEIGHT / 2, slabZ], [HINGE_SIZE, HINGE_HEIGHT, slabDepth], "doorHardware"));
	}

	// ---- openAngle rotation about {hingeX, pivotZ} of the whole assembly (bug E); pivotZ = swing-side face of the slab (≈0 for `in*`, ≈wallThickness for `out*`) so the hinge edge stays put (bug D).
	let movingPlan = moving.build();
	if (openAngle != 0) {
		// pivotZ = the swing-side face of the slab: ≈0 for `in*` (flush inner), ≈wallThickness for `out*` (flush outer).
		const pivotZ = inSwing ? 0 : wt;
		const angleRad = openAngle * (Math.PI / 180) * SwingDirection(swing);
		movingPlan = RotateVertical(movingPlan, hingeX, pivotZ, angleRad);
	}

	// Append the (possibly rotated) slab + hardware.
	b.append(movingPlan);

	return b.build();
}

/** The hinge stile: −1 = the −x (left) end, +1 = the +x (right) end. Left/Right names the hinge
 *  absolutely (not mirrored by `flipped`). */
function HingeSide(swing: DoorSwing): number {
	return swing.endsWith("Left") ? -1 : 1;
}

/** +1/−1 slab rot sign about the hinge axis: `in*`→−z, `out*`→+z, mirrored by hinge side. NEGATED (bug C): `SwingDirection = −(dir · hingeSide)`. */
function SwingDirection(swing: DoorSwing): number {
	const inSwing = swing.startsWith("in");
	const dir = inSwing ? -1 : 1; // into room = −z, out = +z
	return -dir * HingeSide(swing);
}

/** Rotate every vertex + normal of `plan` about the vertical line through (pivotX, pivotZ) by `angleRad`. `pivotZ` = hinge-barrel z (gate: swing-side slab face, §1.2) so the hinge edge stays put (bug D). Pure. */
function RotateVertical(plan: MeshPlan, pivotX: number, pivotZ: number, angleRad: number): MeshPlan {
	const c = Math.cos(angleRad), s = Math.sin(angleRad);
	const out: MeshPlan = {positions: [], normals: [], uvs: [], groups: []};
	const P = plan.positions, N = plan.normals, U = plan.uvs;
	for (let i = 0, k = 0; i < P.length; i += 3, k += 2) {
		const x = P[i], y = P[i + 1], z = P[i + 2];
		const dx = x - pivotX;
		const dz = z - pivotZ;
		const nx = dx * c - dz * s + pivotX;
		const nz = dx * s + dz * c + pivotZ;
		out.positions.push(nx, y, nz);
		// Normals rotate about the axis (no translation component).
		const vx = N[i], vy = N[i + 1], vz = N[i + 2];
		out.normals.push(vx * c - vz * s, vy, vx * s + vz * c);
		out.uvs.push(U[k], U[k + 1]);
	}
	for (const g of plan.groups) out.groups.push({...g});
	return out;
}
